import { Node } from "@xyflow/react";
import { atom } from "jotai";
import { IMPLICIT_GOOD } from "@zapier/canvas-constants";
import { ZslApi } from "@zapier/service-providers";
import {
  aiSuggestedEditsAtomFamily,
  parseZslResponseToNodesAtom,
  nodesAtom,
  edgesAtom,
  saveCanvasSetter,
  isZslExecutionLoadingAtomFamily,
  nodeAtomFamily,
  suggestedNodeHeightsAtomFamily,
  SUGGESTED_NODE_HEIGHT_FALLBACK,
  zslExecutionErrorAtomFamily,
  emitZslExecutionErrorEventSetter,
  nodeLabelAtomFamily,
  emitAiPredictionUserFeedbackEventSetter,
  uuid,
} from "../index";

const VERTICAL_GAP = 70;

function updateSingleNodePosition(newNode: Node, replacedNode: Node): Node {
  return {
    ...newNode,
    position: replacedNode.position,
  };
}

function calculateNextY(currentY: number, height: number, gap: number): number {
  return currentY + height + gap;
}

export const replaceDefaultWithAiSuggestedSetter = atom(
  null,
  async (get, set, nodeId: string) => {
    set(isZslExecutionLoadingAtomFamily(nodeId), true);
    set(zslExecutionErrorAtomFamily(nodeId), null);

    try {
      const nodeToBeReplaced = get(nodeAtomFamily(nodeId));
      if (!nodeToBeReplaced) {
        throw new Error("Node to be replaced not found");
      }

      const aiSuggestion = get(aiSuggestedEditsAtomFamily(nodeId));
      if (!aiSuggestion) {
        throw new Error("No AI suggestions found");
      }

      // Transform the ZSL data by merging all additions with the same key
      const zsl = {
        name: "New Template",
        parameters: [],
        interfaces: aiSuggestion.additions
          .filter((addition) => addition.zsl?.interfaces)
          .flatMap((addition) => addition.zsl?.interfaces || []),
        tables: aiSuggestion.additions
          .filter((addition) => addition.zsl?.tables)
          .flatMap((addition) => addition.zsl?.tables || []),
        zaps: aiSuggestion.additions
          .filter((addition) => addition.zsl?.zaps)
          .flatMap((addition) => addition.zsl?.zaps || []),
      };

      const response = await ZslApi.executeZsl({
        template: zsl,
      });
      const parseZslResponseToNodes = get(parseZslResponseToNodesAtom);
      const responseNodes = parseZslResponseToNodes(response);

      // Emit user feedback event when user explicitly chooses to replace
      set(emitAiPredictionUserFeedbackEventSetter, {
        prediction_id: aiSuggestion.additions[0]?.id || "",
        user_rating: IMPLICIT_GOOD,
        user_feedback: null,
        correlation_id: null,
      });

      set(aiSuggestedEditsAtomFamily(nodeId), null);
      const nodes = get(nodesAtom);
      const edges = get(edgesAtom);

      // Filter out the selected node from existing nodes
      const existingNodes = nodes.filter(
        (node) => node.id !== nodeToBeReplaced.id,
      );

      // Filter out edges connected to the selected node
      const edgesToLayout = edges.filter(
        (edge) =>
          edge.source !== nodeToBeReplaced.id &&
          edge.target !== nodeToBeReplaced.id,
      );

      // Create new edges to connect the new nodes to the same nodes the replaced node was connected to
      const newEdges = edges
        .filter(
          (edge) =>
            edge.source === nodeToBeReplaced.id ||
            edge.target === nodeToBeReplaced.id,
        )
        .flatMap((edge) => {
          const id = uuid();

          if (edge.source === nodeToBeReplaced.id) {
            // If selected node was the source, connect first new node to the target
            return responseNodes.length > 0
              ? [
                  {
                    ...edge,
                    id,
                    source: responseNodes[0].id,
                  },
                ]
              : [];
          } else {
            // If selected node was the target, connect last new node to the source
            return responseNodes.length > 0
              ? [
                  {
                    ...edge,
                    id,
                    target: responseNodes[responseNodes.length - 1].id,
                  },
                ]
              : [];
          }
        });

      // Combine all edges
      const allEdges = [...edgesToLayout, ...newEdges];

      let layoutedNodes;
      if (responseNodes.length === 1) {
        layoutedNodes = [
          updateSingleNodePosition(responseNodes[0], nodeToBeReplaced),
        ];
      } else {
        /**
         * Positions AI-suggested nodes sequentially below the node being replaced.
         * Each node is placed based on the current Y-coordinate, its height, and the vertical gap.
         */
        const suggestedNodeHeights = get(
          suggestedNodeHeightsAtomFamily(nodeId),
        );
        const baseX = nodeToBeReplaced.position.x;
        let currentY = nodeToBeReplaced.position.y;

        layoutedNodes = responseNodes.map((node, index) => {
          const height =
            suggestedNodeHeights[index] !== undefined
              ? suggestedNodeHeights[index]
              : SUGGESTED_NODE_HEIGHT_FALLBACK;

          const newNode = {
            ...node,
            position: {
              x: baseX,
              y: currentY,
            },
          };

          // Update currentY for the next node
          currentY = calculateNextY(currentY, height, VERTICAL_GAP);
          return newNode;
        });
      }

      // Update the state
      const updatedNodes = [...existingNodes, ...layoutedNodes];
      set(nodesAtom, updatedNodes);
      set(edgesAtom, allEdges);
      set(saveCanvasSetter, {
        nodes: updatedNodes,
        edges: allEdges,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "An error occurred while replacing the suggestion";

      // Use the actual ZSL from the error if it exists
      const zslError = error as { zsl?: string };
      const aiSuggestion = get(aiSuggestedEditsAtomFamily(nodeId));
      const zsl =
        zslError.zsl || JSON.stringify(aiSuggestion?.additions[0]?.zsl || {});
      const prompt = (get(nodeLabelAtomFamily(nodeId)) as string) ?? "";

      // Set the error in the atom family
      set(zslExecutionErrorAtomFamily(nodeId), {
        message: errorMessage,
        type: "error",
        zsl,
        prompt,
      });

      // Emit the error event for tracking
      set(emitZslExecutionErrorEventSetter, {
        error_message: errorMessage,
        prompt,
        zsl,
        status_code: 500,
        is_ai_page: false,
        is_template_page: false,
      });

      throw error; // Re-throw to maintain error propagation
    } finally {
      set(isZslExecutionLoadingAtomFamily(nodeId), false);
    }
  },
);

replaceDefaultWithAiSuggestedSetter.debugLabel =
  "replaceDefaultWithAiSuggestedSetter";
